import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../common/Loader";
import AppBar from "../../common/AppBar";
import {
  getAllAttachmentsForStep,
  getAllCanvasesForStep,
  getAllFormsForStep,
  getAllRichTextsForStep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  // Import substep-specific actions if needed, though they are not used in the provided snippet
  getAllAttachmentsForSubstep,
  getAllCanvasesForSubstep,
  getAllFormsForSubstep,
  getAllRichTextsForSubstep,
  getAllVideosForSubstep,
} from "../../redux/stepSlice";
import SetTaskInfo from "../Tasks/SetTaskInfo";
import VersionHistoryAppBar from "../../common/VersionHistoryAppBar";
import {
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../SectionElements/plugins/utils.js";
import RectangleElement from "../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../Canvas/components/arrow/DoubleSidedArrowElement.jsx";

import { getAllSectionsForPart } from "../../redux/sectionSlice.js";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { setStepTreeNodeId } from "../../redux/partSlice.js";
import { getPages, renderPreview, renderVideo } from "./plugins/utils.js";
import CanvasDisplay from "../Canvas/components/canvas-core/CanvasDisplay.jsx";

const VersionHistoryDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  let substepCounter = 1;
  let stepCounter = 1;
  let richTextsCounter = {};
  // Helper function to get a consistent unique ID for any item
  const getNodeId = (item) =>
    item?.id ||
    item?.rich_text_tree_node_id ||
    item?.rich_text_node_id ||
    item?.attachment_tree_node_id ||
    item?.form_tree_node_id ||
    item?.canvas_tree_node_id ||
    item?.video_tree_node_id;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    videos,
    canvases,
    loading,
    error,
  } = useSelector((state) => state.step);

  let attachmentCount = useRef(1);
  let canvasCount = useRef(1);
  let formCount = useRef(1);
  let richTextCount = useRef(1);
  let videoCount = useRef(1);

  const { sections } = useSelector((state) => state.section);
  const revisionData = [];
  const {
    versionHistoryData,
    sectionId,
    partNumber,
    revision,
    revisionId,
    siblingOrder,
    stepId,
    section,
    partId,
    title,
    savedDetails,
  } = location.state || {};
  const selectedVersionId = location.state?.selectedVersionId || "";
  const versionContent = versionHistoryData?.version_content || {};

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Initialize counters from version content or use defaults
  // These counters will now be used within the combined rendering logic
  let attachmentCounter = versionContent.attachmentCounter || 1;
  let canvasCounter = versionContent.canvasCounter || 1;
  let formCounter = versionContent.formCounter || 1;
  let richTextCounter = versionContent.richTextCounter || 1;
  let videoCounter = versionContent.videoCounter || 1;

  useEffect(() => {
    if (partId && revisionId) {
      dispatch(getAllSectionsForPart({ partId, revisionId }));
    }
  }, [dispatch, partId, revisionId]);
  const calcTotalNodesCount = (array) => {
    const count = Object.values(array).reduce((acc, arr) => {
      return acc + arr.length;
    }, 0);

    return count;
  };

  const computeCumulativeMediaCounters = (
    sortedSections,
    stepsBySection,
    endIndex
  ) => {
    const addMediaCounts = (nodeId, acc) => {
      if (attachments[nodeId]?.length)
        acc.attachmentCounter += attachments[nodeId].length;
      if (videos[nodeId]?.length) acc.videoCounter += videos[nodeId].length;
      if (forms[nodeId]?.length) acc.formCounter += forms[nodeId].length;
      if (canvases[nodeId]?.length)
        acc.canvasCounter += canvases[nodeId].length;
      if (richTexts[nodeId]?.length)
        acc.richTextCounter += richTexts[nodeId].length;
    };

    const processSubstepsRecursively = (stepId, acc) => {
      const substeps = substepsByStep[stepId] || [];
      substeps.forEach((substep) => {
        const substepId = substep.sub_step_tree_node_id;
        addMediaCounts(substepId, acc);

        // Recurse if substep itself has substeps
        if (substepsByStep[substepId]?.length) {
          processSubstepsRecursively(substepId, acc);
        }
      });
    };

    return sortedSections.slice(0, endIndex).reduce(
      (acc, section) => {
        const steps = stepsBySection[section.section_tree_node_id] || [];

        steps.forEach((step) => {
          const stepId = step.step_tree_node_id;
          addMediaCounts(stepId, acc);
          processSubstepsRecursively(stepId, acc);
        });

        return acc;
      },
      {
        attachmentCounter: 0,
        videoCounter: 0,
        formCounter: 0,
        canvasCounter: 0,
        richTextCounter: 0,
      }
    );
  };

  useEffect(() => {
    const fetchStepsForSections = async () => {
      if (sections.length > 0) {
        await Promise.all(
          sections.map((section) =>
            dispatch(
              getAllStepsForSection({
                partId,
                revisionId,
                sectionId: section.section_tree_node_id,
              })
            )
          )
        );
      }
    };

    fetchStepsForSections();
  }, [sections, dispatch, partId, revisionId]);

  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoom = (factor) => {
    const rawZoom = zoomLevel * factor;
    const clampedZoom = Math.max(0.1, Math.min(rawZoom, 5)); // Clamp between 0.1 and 5
    const roundedZoom = Math.round(clampedZoom * 10) / 10; // Round to nearest 0.1 (10%)
    setZoomLevel(roundedZoom);
  };

  const handleManualZoom = (e) => {
    const input = parseFloat(e.target.value);
    if (!isNaN(input)) {
      const clampedInput = Math.max(10, Math.min(input, 500)); // Clamp 10% to 500%
      const roundedInput = Math.round(clampedInput / 10) * 10; // Nearest multiple of 10
      setZoomLevel(roundedInput / 100); // Store as decimal
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId,
          })
        );

        const stepActions = [
          getAllRichTextsForStep,
          getAllAttachmentsForStep,
          getAllFormsForStep,
          getAllVideosForStep,
          getAllCanvasesForStep,
        ];

        const substepActions = [
          getAllRichTextsForSubstep,
          getAllAttachmentsForSubstep,
          getAllFormsForSubstep,
          getAllVideosForSubstep,
          getAllCanvasesForSubstep,
        ];

        const allFetches = [];

        // Fetch for current step
        for (const action of stepActions) {
          allFetches.push(
            dispatch(
              action({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId: null,
              })
            )
          );
        }

        // Fetch substeps and their content
        const substepsResponse = await dispatch(
          getAllSubstepsForStep({
            partId,
            revisionId,
            sectionId,
            stepId,
          })
        ).unwrap();

        const fetchedSubsteps = substepsResponse?.substeps || [];

        for (const substep of fetchedSubsteps) {
          const substepId = substep.sub_step_tree_node_id;
          for (const action of substepActions) {
            allFetches.push(
              dispatch(
                action({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              )
            );
          }
        }

        await Promise.all(allFetches);
      } catch (err) {
        setErrorMessage(
          err.message || "Failed to load step data. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (partId && revisionId && sectionId) {
      fetchData();
    }
  }, [dispatch, partId, revisionId, sectionId, stepId]);

  const step = location.state?.step || {};

  if (step) {
    SetTaskInfo(step.step_tree_node_id, step.procedure_tree?.tree_node_id);
  }

  const getAdjustedSiblingOrder = (parentOrder, children) => {
    // Safety check: ensure children is an array
    const childrenArray = children || [];

    const validSiblingOrders = childrenArray
      .map(
        (child) => child.sibling_order || child.procedure_tree?.sibling_order
      )
      .filter(Boolean)
      .map((order) => parseInt(order, 10));

    if (validSiblingOrders.length === 0) {
      // No children, return parent + 1
      return `${parseInt(parentOrder) + 1}, 1`;
    }
    // Get the highest sibling order and increment it
    const highestOrder = Math.max(...validSiblingOrders);

    return `${parseInt(parentOrder) + 1}, ${(highestOrder + 1).toString()}`;
  };

  if (isLoading) {
    return <Loader text="Loading version history details..." />;
  }

  if (errorMessage) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            Error Loading Version History Details
          </h4>
          <p>{errorMessage}</p>
          <hr />
          <p className="mb-0">
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (!step) {
    return <Loader text="Loading step data..." />;
  }

  // Combine and sort content for a given parent ID (stepId or substepId)
  const getSortedContent = (parentId) => {
    const combinedItems = [
      ...(richTexts[parentId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[parentId] || []).map((item) => ({
        ...item,
        type: "form",
      })),
      // Flatten attachments from their wrappers
      ...(attachments[parentId] || []).flatMap((wrapper) =>
        (wrapper.attachments || []).map((att) => ({
          ...att,
          type: "attachment",
          // Keep reference to the wrapper's tree node ID if needed for context
          attachment_wrapper_tree_node_id: wrapper.attachment_tree_node_id,
        }))
      ),
      ...(canvases[parentId] || []).map((item) => ({
        ...item,
        type: "canvas",
      })),
      ...(videos[parentId] || []).map((item) => ({
        ...item,
        type: "video",
      })),
    ];

    // Deduplicate items based on their unique ID
    const uniqueItems = [];
    const seenIds = new Set();
    combinedItems.forEach((item) => {
      const id = getNodeId(item);
      if (id && !seenIds.has(id)) {
        uniqueItems.push(item);
        seenIds.add(id);
      }
    });

    // Sort by sibling_order, then by created_at as fallback
    uniqueItems.sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;

      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateA - dateB; // Sort by oldest first for consistent display
    });

    return uniqueItems;
  };

  const renderContentItem = (
    item,
    parentType,
    parentIndex,
    parentGlobalNumber
  ) => {
    let label;
    let content;
    let key = getNodeId(item); // Use the consistent ID for key

    switch (item.type) {
      case "richText": {
        richTextCounter++; // Increment counter for this type
        label = `Rich Text ${richTextCounter}`;
        content = (
          <>
            <div style={{ marginLeft: "5px", marginTop: "-10px", whiteSpace: 'pre-wrap' }}>
              {item.short_description}
            </div>
            <div style={{ marginLeft: "4px", marginTop: "5px" }}>
              {item.category_data?.category_name ||
                item.category_info?.category_name}
            </div>
            <div
              style={{ fontSize: "14px", marginLeft: "4px", marginTop: "5px" }}
            >
              <div
                style={{ marginTop: "-6px" }}
                dangerouslySetInnerHTML={{ __html: item.rich_text_content }}
              />
            </div>
          </>
        );
        break;
      }
      case "form": {
        formCounter++; // Increment counter
        label = `Form ${formCounter}`;
        content = (
          <>
                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
          </>
        );
        break;
      }
      case "video": {
        videoCounter++; // Increment counter
        label = `Video ${videoCounter}`;
        content = renderPreview(item, `Video ${videoCounter}`);
        break;
      }
      case "canvas": {
        canvasCounter++; // Increment counter
        label = `Canvas ${canvasCounter}`;
        const shapes = item.shapes || [];
        const pages = getPages(item);

        content = (
          <>
                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
            {/* Display all pages vertically */}
            <CanvasDisplay pages={pages} />
          </>
        );
        break;
      }
      case "attachment": {
        attachmentCounter++; // Increment counter
        label = `Attachment ${attachmentCounter}`;
        content = renderPreview(item, `Attachment ${attachmentCounter}`);
        break;
      }
      default:
        return null;
    }

    return (
      <div key={key} style={{ marginTop: "20px" }}>
        <label
          style={{
            display: "flex",
            gap: "6px",
            cursor: "pointer",
          }}
        >
          <input type="checkbox" disabled={true} checked={true} />
          <h2>{label}</h2>
        </label>
        {content}
      </div>
    );
  };
  const description = location.state?.description || "";
  const type = location.state?.type || "";

  const breadcrumb_text = `${partNumber} > Rev ${revision} > ${section} > ${title}`;
  const sortedSections =
    [...sections]?.sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;

      // Fallback: compare created_at
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA; // latest comes first
    }) || [];

  return (
    <div>
      <VersionHistoryAppBar
        title={`${title} (Version ${selectedVersionId})`}
        siblingOrder={siblingOrder}
        partType={location.state?.type || location.state?.partType || ""}
        data={location.state?.data || {}}
        savedDetailsForReviewing={location.state.savedDetailsForReviewing || ""}
        section={section}
        savedDetails={location.state.savedDetails || ""}
        stepTitle={title}
        elementTitle={title}
        id={location.state?.id || ""}
        sectionId={sectionId}
        revisionId={revisionId}
        partId={partId}
        revision={revision}
        partNumber={partNumber}
        stepId={stepId}
        description={
          location.state?.description || location.state?.data?.description || ""
        }
        versionHistoryData={location.state?.versionHistoryData}
        sibling
      />

      <div style={{ padding: "20px" }}>
        {/* Procedure Status and Revision Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            padding: "8px 2px",
            flexWrap: "wrap", // for responsiveness
          }}
        >
          {/* Checkbox + Title + Description */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                margin: 0,
              }}
            >
              <input type="checkbox" disabled={true} />
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  marginTop: "-10px",
                  color: "#333",
                  opacity: 1,
                }}
              >
                {type} {partNumber}
              </span>
            </label>
            <span
              style={{
                fontSize: "10px",
                color: "#0d6efd",
                fontWeight: "bold",
                marginLeft: "30px",
                marginTop: "-6px",
              }}
            >
              {savedDetails}
            </span>
          </div>

          {/* Right-aligned section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginLeft: "auto",
            }}
          >
            <div>
              <strong>Status: </strong>
              <span>{location.state?.status || ""}</span>
            </div>

            <div>
              <strong>Revision: </strong>
              <span>{revision}</span>
            </div>
          </div>
        </div>

        {sortedSections
          .sort((a, b) => {
            const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
            const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

            const orderCompare = compareSiblingOrders(orderA, orderB);
            if (orderCompare !== 0) return orderCompare;

            // Fallback: compare created_at
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA; // latest comes first
          })
          .map((section, index) => {
            if (
              location.state.sectionId &&
              section.section_tree_node_id !== location.state.sectionId
            ) {
              return;
            }
            return (
              <div
                key={section.section_tree_node_id}
                style={{
                  margin: "20px 0",
                  marginTop: "150px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={title != `Section ${index + 1}`}
                  />

                  <h2 style={{ margin: 0 }}>Section {index + 1}</h2>
                </label>
                <hr />
                <hr />
                <div>{section.section_name}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{section.short_description}</div>

                {/* Steps for this section */}
                {(stepsBySection[section.section_tree_node_id] || []).map(
                  (step, stepIndex) => {
                    const stepId = step.step_tree_node_id;

                    if (
                      location.state.stepId &&
                      stepId !== location.state.stepId
                    ) {
                      return;
                    }
                    const sectionId = section.section_tree_node_id;
                    stepCounter++;
                    const stepsBeforeThisSection = sortedSections
                      .slice(0, index)
                      .reduce(
                        (acc, s) =>
                          acc +
                          (stepsBySection[s.section_tree_node_id]?.length || 0),
                        0
                      );
                    const globalStepNumber =
                      stepsBeforeThisSection + stepIndex + 1;

                    const substeps = substepsByStep[stepId] || [];

                    const substepLetter = String.fromCharCode(
                      65 + substeps.length
                    ); // A, B, C...

                    // Calculate media counters before navigation
                    const currentAttachmentCounter = attachmentCounter;
                    const currentVideoCounter = videoCounter;
                    const currentFormCounter = formCounter;
                    const currentCanvasCounter = canvasCounter;
                    const currentRichTextCounter = richTextCounter;

                    // Update counters for next step
                    //attachmentCounter += myAttachments.length;
                    // videoCounter += myVideos.length;
                    // formCounter += myForms.length;
                    // canvasCounter += myCanvases.length;
                    // richTextCounter += myRichTexts.length; // If you're adding one richText per step

                    richTextsCounter[stepId] = currentRichTextCounter;

                    const combinedItems = [
                      ...(richTexts[stepId] || []).map((item) => ({
                        ...item,
                        type: "richText",
                      })),
                      ...(forms[stepId] || []).map((item) => ({
                        ...item,
                        type: "form",
                      })),
                      ...(attachments[stepId] || []).map((item) => ({
                        ...item,
                        type: "attachment",
                      })),
                      ...(canvases[stepId] || []).map((item) => ({
                        ...item,
                        type: "canvas",
                      })),
                      ...(videos[stepId] || []).map((item) => ({
                        ...item,
                        type: "video",
                      })),
                    ]; // Filter out substeps or unwanted items (same as your current filter)
                    const filteredItems = combinedItems.filter(
                      (item) =>
                        !item.substep_id_ref && !item.sub_step_tree_node_id
                    );

                    filteredItems.sort((a, b) => {
                      const orderA =
                        a.sibling_order || a.procedure_tree?.sibling_order;
                      const orderB =
                        b.sibling_order || b.procedure_tree?.sibling_order;

                      const orderCompare = compareSiblingOrders(orderA, orderB);
                      if (orderCompare !== 0) return orderCompare;

                      // Fallback: compare created_at
                      const dateA = new Date(a.created_at);
                      const dateB = new Date(b.created_at);
                      return dateB - dateA; // latest comes first
                    });
                    const allChildrenUnderStep = [
                      ...(richTexts[stepId] || []),
                      ...(forms[stepId] || []),
                      ...(attachments[stepId] || []),
                      ...(canvases[stepId] || []),
                      ...(videos[stepId] || []),
                      ...(substepsByStep[stepId] || []),
                    ];

                    return (
                      <div key={stepId} style={{ marginTop: "20px" }}>
                        <div>
                          <label
                            style={{
                              display: "flex",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={title != `Step ${globalStepNumber}`}
                            />

                            <h2 style={{ margin: 0 }}>
                              Step {globalStepNumber}
                            </h2>
                          </label>

                          <div
                            style={{
                              color: "#888",
                              fontSize: "14px",
                              fontWeight: "normal",
                            }}
                          >
                            Section {index + 1} &gt; Step {globalStepNumber}
                          </div>
                        </div>
                        <hr />
                        <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                        <p>
                          <p>
                            {structuredClone(step.step_details)
                              .sort((a, b) => {
                                const orderA = parseInt(a.orders || 0, 10);
                                const orderB = parseInt(b.orders || 0, 10);
                                if (orderA !== orderB) return orderA - orderB;
                                // Fallback to created_at if orders are equal or missing
                                return (
                                  new Date(a.created_at) -
                                  new Date(b.created_at)
                                );
                              })
                              .map((myStep) => {
                                const partNumber =
                                  myStep.part_info?.part_number || "";
                                const description =
                                  myStep.part_info?.description || "";
                                const qtyText =
                                  myStep.qty && myStep.qty != 0
                                    ? `Qty ${myStep.qty}`
                                    : "";

                                const partText = partNumber
                                  ? ` (${partNumber})`
                                  : "";

                                return `${myStep.action}${partText} ${description} ${qtyText}`.trim();
                              })
                              .join(" ")}
                          </p>
                        </p>

                        <h4>
                          {step.bom_table_title_info?.category_name || ""}
                        </h4>

                        <table className="table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>
                                {
                                  step.part_number_column_title_ref_info
                                    ?.category_name
                                }{" "}
                              </th>
                              <th>Description</th>
                              <th>Qty Per</th>
                              <th>Unit</th>
                            </tr>
                          </thead>

                          <tbody>
                            {step.parts_required.map((row) => (
                              <tr>
                                <td>{row.part_info.type_info.name}</td>
                                <td>{row.part_info.part_number}</td>
                                <td>{row.part_info.description}</td>
                                <td>{row.qty === 0 ? "" : row.qty}</td>
                                <td>{row.part_info?.unit_info?.unit_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Step Content */}

                        {filteredItems.map((item, childIndex) => {
                          if (item.substep_id_ref || item.sub_step_tree_node_id)
                            return null;
                          switch (item.type) {
                            case "richText": {
                              const globalRichTextNumber = richTextCounter++; // ✅ use then increment
                              // render richText block here
                              const label = (
                                <h2>Rich Text {globalRichTextNumber}</h2>
                              );

                              const newSiblingOrderForRichText =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div
                                  key={item.rich_text_node_id}
                                  style={{ marginTop: "20px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title !=
                                          `Rich Text ${globalRichTextNumber}`
                                        }
                                      />
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Rich Text{" "}
                                      {globalRichTextNumber}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      marginLeft: "5px",
                                      marginTop: "-10px",
                                    }}
                                  >
                                    {item.short_description}
                                  </div>
                                  <div
                                    style={{
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {item.category_data
                                      ? item.category_data.category_name
                                      : item.category_info.category_name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    <div
                                      style={{ marginTop: "-6px" }}
                                      dangerouslySetInnerHTML={{
                                        __html: item.rich_text_content,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            }
                            case "video": {
                              const globalVideoNumber = videoCounter++; // ✅ use then increment
                              const label = <h2>Video {globalVideoNumber}</h2>;

                              const newSiblingOrderForVideo =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div
                                  key={
                                    item.video_tree_node_id ||
                                    item.video_tree_node_ref
                                  }
                                  style={{ marginTop: "20px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title != `Video ${globalVideoNumber}`
                                        }
                                      />
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Video{" "}
                                      {globalVideoNumber}
                                    </div>
                                  </div>

                                  {renderVideo(item)}

                                  <div>{item.video_url}</div>

                                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                </div>
                              );
                            }

                            case "form": {
                              const globalFormNumber = formCounter++; // ✅ use then increment
                              const label = <h2>Form {globalFormNumber}</h2>;
                              // render form block here
                              return (
                                <div key={item.form_tree_node_id}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                        marginTop: "20px",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title != `Form ${globalFormNumber}`
                                        }
                                      />

                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Form{" "}
                                      {globalFormNumber}
                                    </div>
                                  </div>
                                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                </div>
                              );
                            }
                            case "canvas": {
                              const pages = getPages(item);

                              const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                              const label = (
                                <h3>Canvas {globalCanvasNumber}</h3>
                              );
                              const shapes = item.shapes || [];

                              const newSiblingOrderForCanvas =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div
                                  key={item.canvas_tree_node_id}
                                  style={{ marginTop: "40px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title !=
                                          `Canvas ${globalCanvasNumber}`
                                        }
                                      />

                                      <h2>{label}</h2>
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Canvas{" "}
                                      {globalCanvasNumber}
                                    </div>
                                  </div>

                                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>

                                  {/* Display all pages vertically */}
                                  <CanvasDisplay
                                    pages={pages}
                                    hasUserContent={hasUserContent}
                                    getTextStyle={getTextStyle}
                                    stripHtmlPreserveFormatting={
                                      stripHtmlPreserveFormatting
                                    }
                                    estimateKonvaTextHeight={
                                      estimateKonvaTextHeight
                                    }
                                    formatListContent={formatListContent}
                                    hasNoteContent={hasNoteContent}
                                    hasTaskContent={hasTaskContent}
                                    calculateGroupHeight={calculateGroupHeight}
                                    calculateTextBlockWidth={
                                      calculateTextBlockWidth
                                    }
                                  />
                                </div>
                              );
                            }
                            case "attachment": {
                              const attachmentList = item.attachments || [];
                              if (attachmentList.length === 0) return null;

                              //  const globalAttachmentNumber =
                              //           attachmentCounter++; // ✅ use then increment
                              const globalAttachmentNumber =
                                attachmentCounter++; // ✅ Increment here per attachment
                              const label = (
                                <h2>Attachment {globalAttachmentNumber}</h2>
                              );
                              const newSiblingOrderForAttachment =
                                getAdjustedSiblingOrder(
                                  item.sibling_order || "null",
                                  allChildrenUnderStep
                                );

                              return (
                                <div key={item.attachment_tree_node_id}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                        marginTop: "20px",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title !=
                                          `Attachment ${globalAttachmentNumber}`
                                        }
                                      />
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Attachment{" "}
                                      {globalAttachmentNumber}
                                    </div>
                                  </div>
                                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                  {attachmentList.map((attachment, i) => (
                                    <div key={i} style={{ marginLeft: "20px" }}>
                                      {renderPreview(attachment)}
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            default:
                              return null;
                          }
                        })}

                        {/* Substeps */}
                        {(substepsByStep[stepId] || []).map(
                          (substep, substepIndex) => {
                            const substepId = substep.sub_step_tree_node_id;
                            const combinedItems = [
                              ...(richTexts[substepId] || []).map((item) => ({
                                ...item,
                                type: "richText",
                              })),
                              ...(forms[substepId] || []).map((item) => ({
                                ...item,
                                type: "form",
                              })),
                              ...(attachments[substepId] || []).map((item) => ({
                                ...item,
                                type: "attachment",
                              })),
                              ...(canvases[substepId] || []).map((item) => ({
                                ...item,
                                type: "canvas",
                              })),
                              ...(videos[substepId] || []).map((item) => ({
                                ...item,
                                type: "video",
                              })),
                            ]; // Filter out substeps or unwanted items (same as your current filter)
                            const filteredItems = combinedItems;

                            filteredItems.sort((a, b) => {
                              const orderA =
                                a.sibling_order ||
                                a.procedure_tree?.sibling_order;
                              const orderB =
                                b.sibling_order ||
                                b.procedure_tree?.sibling_order;

                              const orderCompare = compareSiblingOrders(
                                orderA,
                                orderB
                              );
                              if (orderCompare !== 0) return orderCompare;

                              // Fallback: compare created_at
                              const dateA = new Date(a.created_at);
                              const dateB = new Date(b.created_at);
                              return dateB - dateA; // latest comes first
                            });

                            // Global step number (same logic you already use for steps)
                            const stepsBeforeThisOne =
                              sortedSections
                                .slice(0, index)
                                .reduce((sectionAcc, s) => {
                                  const steps =
                                    stepsBySection[s.section_tree_node_id] ||
                                    [];
                                  return sectionAcc + steps.length;
                                }, 0) + stepIndex;

                            const globalStepNumber = stepsBeforeThisOne + 1;

                            // Final substep label like Substep 5A
                            const substepLetter = String.fromCharCode(
                              65 + substepIndex
                            ); // A, B, C, ...
                            const newSubstepLetter = String.fromCharCode(
                              65 + substepIndex + 1
                            );
                            const allChildrenUnderSubstep = [
                              ...(richTexts[substepId] || []),
                              ...(forms[substepId] || []),
                              ...(attachments[substepId] || []),
                              ...(canvases[substepId] || []),
                              ...(videos[substepId] || []),
                              ...(substepsByStep[substepId] || []),
                            ];
                            const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;
                            const newSiblingOrder = getAdjustedSiblingOrder(
                              substep.sibling_order || "null",
                              allChildrenUnderSubstep
                            );
                            const globalRichTextNumber = richTextCounter; // ✅ use then increment
                            const globalAttachmentNumber = attachmentCounter;
                            const globalVideoNumber = videoCounter;
                            const globalCanvasNumber = canvasCounter;
                            const globalFormNumber = formCounter;

                            return (
                              <div
                                key={substepId}
                                style={{ marginTop: "20px" }}
                              >
                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={
                                          title != `${globalSubstepLabel}`
                                        }
                                      />
                                      <h2 style={{ margin: 0 }}>
                                        {globalSubstepLabel}
                                      </h2>
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Substep{" "}
                                      {globalStepNumber}
                                      {substepLetter}
                                    </div>
                                  </div>

                                  <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                                </div>

                                {/* Substep Content */}
                                {filteredItems.map((item, childIndex) => {
                                  switch (item.type) {
                                    case "richText": {
                                      const globalRichTextNumber =
                                        richTextCounter++; // ✅ use then increment
                                      // render richText block here
                                      const label = (
                                        <h2>
                                          Rich Text {globalRichTextNumber}
                                        </h2>
                                      );

                                      const newSiblingOrderForRichText =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={item.rich_text_node_id}
                                          style={{ marginTop: "20px" }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <label
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={
                                                  title !=
                                                  `Rich Text ${globalRichTextNumber}`
                                                }
                                              />
                                              {label}
                                            </label>
                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                              }}
                                            >
                                              Section {index + 1} &gt; Step{" "}
                                              {globalStepNumber} &gt; Substep{" "}
                                              {globalStepNumber}
                                              {substepLetter} &gt; Rich Text{" "}
                                              {globalRichTextNumber}
                                            </div>
                                          </div>

                                          <div
                                            style={{
                                              marginLeft: "5px",
                                              marginTop: "-10px",
                                            }}
                                          >
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                          </div>
                                          <div
                                            style={{
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                            }}
                                          >
                                            {item.category_data
                                              ? item.category_data.category_name
                                              : item.category_info
                                                  .category_name}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              marginLeft: "4px",
                                              marginTop: "5px",
                                            }}
                                          >
                                            <div
                                              style={{ marginTop: "-6px" }}
                                              dangerouslySetInnerHTML={{
                                                __html: item.rich_text_content,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                    case "video": {
                                      const globalVideoNumber = videoCounter++; // ✅ use then increment
                                      const label = (
                                        <h2>Video {globalVideoNumber}</h2>
                                      );

                                      const newSiblingOrderForVideo =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={
                                            item.video_tree_node_id ||
                                            item.video_tree_node_ref
                                          }
                                          style={{ marginTop: "20px" }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <label
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={
                                                  title !=
                                                  `Video ${globalVideoNumber}`
                                                }
                                              />
                                              {label}
                                            </label>

                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                              }}
                                            >
                                              Section {index + 1} &gt; Step{" "}
                                              {globalStepNumber} &gt; Substep{" "}
                                              {globalStepNumber}
                                              {substepLetter} &gt; Video{" "}
                                              {globalVideoNumber}
                                            </div>
                                          </div>

                                          {renderVideo(item)}

                                          <div>{item.video_url}</div>

                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                        </div>
                                      );
                                    }

                                    case "form": {
                                      const globalFormNumber = formCounter++; // ✅ use then increment
                                      const label = (
                                        <h2>Form {globalFormNumber}</h2>
                                      );
                                      // render form block here
                                      return (
                                        <div key={item.form_tree_node_id}>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <label
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                cursor: "pointer",
                                                marginTop: "20px",
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={
                                                  title !=
                                                  `Form ${globalFormNumber}`
                                                }
                                              />

                                              {label}
                                            </label>

                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                              }}
                                            >
                                              Section {index + 1} &gt; Step{" "}
                                              {globalStepNumber} &gt; Substep{" "}
                                              {globalStepNumber}
                                              {substepLetter} &gt; Form{" "}
                                              {globalFormNumber}
                                            </div>
                                          </div>
                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                        </div>
                                      );
                                    }
                                    case "canvas": {
                                      const pages = getPages(item);

                                      const globalCanvasNumber =
                                        canvasCounter++; // ✅ use then increment
                                      const label = (
                                        <h3>Canvas {globalCanvasNumber}</h3>
                                      );
                                      const shapes = item.shapes || [];

                                      const newSiblingOrderForCanvas =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div
                                          key={item.canvas_tree_node_id}
                                          style={{ marginTop: "40px" }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <label
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={
                                                  title !=
                                                  `Canvas ${globalCanvasNumber}`
                                                }
                                              />

                                              <h2>{label}</h2>
                                            </label>

                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                              }}
                                            >
                                              Section {index + 1} &gt; Step{" "}
                                              {globalStepNumber} &gt; Substep{" "}
                                              {globalStepNumber}
                                              {substepLetter} &gt; Canvas{" "}
                                              {globalCanvasNumber}
                                            </div>
                                          </div>

                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>

                                          {/* Display all pages vertically */}
                                          <CanvasDisplay
                                            pages={pages}
                                            hasUserContent={hasUserContent}
                                            getTextStyle={getTextStyle}
                                            stripHtmlPreserveFormatting={
                                              stripHtmlPreserveFormatting
                                            }
                                            estimateKonvaTextHeight={
                                              estimateKonvaTextHeight
                                            }
                                            formatListContent={
                                              formatListContent
                                            }
                                            hasNoteContent={hasNoteContent}
                                            hasTaskContent={hasTaskContent}
                                            calculateGroupHeight={
                                              calculateGroupHeight
                                            }
                                            calculateTextBlockWidth={
                                              calculateTextBlockWidth
                                            }
                                          />
                                        </div>
                                      );
                                    }
                                    case "attachment": {
                                      const attachmentList =
                                        item.attachments || [];
                                      if (attachmentList.length === 0)
                                        return null;

                                      //  const globalAttachmentNumber =
                                      //           attachmentCounter++; // ✅ use then increment
                                      const globalAttachmentNumber =
                                        attachmentCounter++; // ✅ Increment here per attachment
                                      const label = (
                                        <h2>
                                          Attachment {globalAttachmentNumber}
                                        </h2>
                                      );
                                      const newSiblingOrderForAttachment =
                                        getAdjustedSiblingOrder(
                                          item.sibling_order || "null",
                                          allChildrenUnderSubstep
                                        );

                                      return (
                                        <div key={item.attachment_tree_node_id}>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <label
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                cursor: "pointer",
                                                marginTop: "20px",
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={
                                                  title !=
                                                  `Attachment ${globalAttachmentNumber}`
                                                }
                                              />
                                              {label}
                                            </label>

                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                              }}
                                            >
                                              Section {index + 1} &gt; Step{" "}
                                              {globalStepNumber} &gt; Substep{" "}
                                              {globalStepNumber}
                                              {substepLetter} &gt; Attachment{" "}
                                              {globalAttachmentNumber}
                                            </div>
                                          </div>
                                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                          {attachmentList.map(
                                            (attachment, i) => (
                                              <div
                                                key={i}
                                                style={{ marginLeft: "20px" }}
                                              >
                                                {renderPreview(attachment)}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      );
                                    }
                                    default:
                                      return null;
                                  }
                                })}
                              </div>
                            );
                          }
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default VersionHistoryDetailsPage;
